import { useState, useEffect } from 'react';
import { cfoDashboardAPI } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { DollarSign, TrendingUp, PiggyBank, BarChart3 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function CFODashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    cfoDashboardAPI.getData().then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4"><div className="h-8 w-48 bg-muted rounded animate-pulse" /><div className="grid grid-cols-3 gap-4">{[1, 2, 3].map(i => <div key={i} className="h-28 bg-muted rounded-xl animate-pulse" />)}</div></div>;

  return (
    <div data-testid="cfo-dashboard-page" className="space-y-6">
      <h1 className="cp-page-title">CFO Dashboard</h1>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="p-5 animate-fade-in stagger-1" data-testid="cfo-compliance-cost">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><DollarSign className="w-5 h-5 text-red-500" /></div>
            <p className="text-sm text-muted-foreground">Total Compliance Cost</p>
          </div>
          <p className="text-2xl font-heading font-bold">${data?.total_compliance_cost?.toLocaleString()}</p>
        </Card>
        <Card className="p-5 animate-fade-in stagger-2" data-testid="cfo-annual-savings">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><PiggyBank className="w-5 h-5 text-green-600" /></div>
            <p className="text-sm text-muted-foreground">Annual Savings</p>
          </div>
          <p className="text-2xl font-heading font-bold text-green-600">${data?.annual_savings?.toLocaleString()}</p>
        </Card>
        <Card className="p-5 animate-fade-in stagger-3" data-testid="cfo-roi">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-accent" /></div>
            <p className="text-sm text-muted-foreground">ROI</p>
          </div>
          <p className="text-2xl font-heading font-bold">{data?.roi}%</p>
        </Card>
      </div>

      <div className="cp-chart-card animate-fade-in" data-testid="cfo-price-forecast">
        <h3 className="text-sm font-heading font-semibold mb-4 flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-muted-foreground" /> Certificate Price Forecast
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data?.certificate_price_forecast || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} tickLine={false} axisLine={false} unit=" EUR" />
              <Tooltip contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, fontSize: 12 }} />
              <Line type="monotone" dataKey="price" stroke="#4F46E5" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
